import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import type { IBlockPayloadSlider } from '../../../types'

const AUTOPLAY_MS = 5000
const TRANSITION_MS = 500

export const SliderRenderer: React.FC<{ payload: IBlockPayloadSlider }> = ({ payload }) => {
  const images = payload.images ?? []
  const [current, setCurrent] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const autoplayRef = useRef<ReturnType<typeof setInterval>>()
  const len = images.length

  const goTo = useCallback(
    (idx: number) => {
      if (isTransitioning || len <= 1) return
      setIsTransitioning(true)
      setCurrent(((idx % len) + len) % len)
      setTimeout(() => setIsTransitioning(false), TRANSITION_MS)
    },
    [isTransitioning, len],
  )

  const prev = useCallback(() => goTo(current - 1), [goTo, current])
  const next = useCallback(() => goTo(current + 1), [goTo, current])

  const resetAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
    if (len > 1) {
      autoplayRef.current = setInterval(() => goTo(current + 1), AUTOPLAY_MS)
    }
  }, [goTo, current, len])

  useEffect(() => {
    resetAutoplay()
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current)
    }
  }, [resetAutoplay])

  if (len === 0) return null

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    bgcolor: 'rgba(255,255,255,0.85)',
    color: 'text.primary',
    width: 40,
    height: 40,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    opacity: 0,
    transition: 'opacity 0.25s',
    '&:hover': { bgcolor: '#fff' },
  } as const

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        '&:hover .slider-arrow': { opacity: 1 },
      }}
    >
      {/* Track */}
      <Box
        sx={{
          display: 'flex',
          width: `${len * 100}%`,
          transform: `translateX(-${(current * 100) / len}%)`,
          transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 1, 0.5, 1)`,
        }}
      >
        {images.map((img, idx) => (
          <Box
            key={idx}
            sx={{ width: `${100 / len}%`, flexShrink: 0 }}
          >
            <Box
              component="img"
              src={img.url}
              alt={img.alt ?? ''}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
              draggable={false}
            />
          </Box>
        ))}
      </Box>

      {/* Arrows */}
      {len > 1 && (
        <>
          <IconButton
            className="slider-arrow"
            onClick={prev}
            sx={{ ...arrowSx, left: 12 }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            className="slider-arrow"
            onClick={next}
            sx={{ ...arrowSx, right: 12 }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Dots */}
      {len > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
          }}
        >
          {images.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => goTo(idx)}
              sx={{
                width: idx === current ? 24 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: idx === current ? '#fff' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: `all ${TRANSITION_MS}ms cubic-bezier(0.25, 1, 0.5, 1)`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: '#fff' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
